/**
 * @file App.jsx
 * @location src/App.jsx
 * @role 루트 컴포넌트 — 페이지 라우팅 + 전역 모달 + 토스트 관리
 *
 * 페이지 전환: S.activeNav 값으로 조건 렌더링 (React Router 없이 직접 스위칭)
 *   'tasks'     → TasksPage   (src/pages/TasksPage.jsx) — isManager 분기 (전원 초기 진입)
 *   'members'   → MembersPage (인원) — 매니저 전용 인원관리 (razor ⑳㉒)
 *   'mypage'    → MyPage (마이 페이지) — 개인 영역, 아바타/⋯ 메뉴 진입, nav 바 미등록 보조 화면
 *
 * 전역 모달: S.modalTask/Prog/Trash/History/Shortcuts/Invite/InviteMember/Routine/Settings 플래그로 열고 닫음
 *   (설정은 매니저 전용 모달 — 아바타/⋯ 메뉴 진입. 이력는 modalHistory)
 * 타임라인 패널: 업무「마이」 우측 인라인 단일 모드 (드로어 모드 폐지 — razor ⑨ 페이지 정체성)
 */
import { useEffect, useMemo, lazy, Suspense } from 'react';
import { useApp } from './store/AppContext';
import { useToast } from './store/useToast';
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useOnlineStatus } from './hooks/useOnlineStatus.js';
import { useGlobalErrorToasts } from './hooks/useGlobalErrorToasts.js';
import { useUnreadBadge } from './hooks/useUnreadBadge.js';
import { usePushPrompt } from './hooks/usePushPrompt.js';
import { useNavGuard } from './hooks/useNavGuard.js';
import { useBootHousekeeping } from './hooks/useBootHousekeeping.js';
import { canManageMembers } from './utils/permissions';
import { track, EVENTS } from './integrations/analytics';
import { useSubscription } from './hooks/useSubscription.js';
import Topbar   from './components/layout/Topbar';
import { OfflineIndicator } from './components/layout/OfflineIndicator.jsx';
import { PushPermissionPrompt } from './components/push/PushPermissionPrompt.jsx';
import { InstallPwaCard } from './components/push/InstallPwaCard.jsx';
import { SubscriptionBanner } from './components/billing/SubscriptionBanner.jsx';
import { TasksPage }   from './pages/TasksPage';
import { ToastStack } from './components/ui/SharedUI';
import { LoadingSpinner } from './components/ui/sharedui/feedback';
import { ErrorBoundary } from './components/ui/ErrorBoundary';

// 페이지 lazy — 자주 접근 안 하는 페이지 (초기 진입은 tasks 만 즉시 로드)
const MembersPage    = lazy(() => import('./pages/MembersPage').then(m => ({ default: m.MembersPage })));
const MyPage         = lazy(() => import('./pages/MyPage').then(m => ({ default: m.MyPage })));

// 모달 lazy — 플래그가 true 가 되어 마운트될 때만 다운로드
const TaskModal         = lazy(() => import('./components/modals/TaskModal').then(m => ({ default: m.TaskModal })));
const ProgModal         = lazy(() => import('./components/modals/ProgModal').then(m => ({ default: m.ProgModal })));
const TrashModal        = lazy(() => import('./components/modals/TrashModal').then(m => ({ default: m.TrashModal })));
const ShortcutsHelpModal = lazy(() => import('./components/modals/ShortcutsHelpModal').then(m => ({ default: m.ShortcutsHelpModal })));
const InviteMemberModal = lazy(() => import('./components/modals/InviteMemberModal').then(m => ({ default: m.InviteMemberModal })));
const RoutineModal      = lazy(() => import('./components/modals/RoutineModal').then(m => ({ default: m.RoutineModal })));
const SettingsModal     = lazy(() => import('./components/modals/SettingsModal').then(m => ({ default: m.SettingsModal })));
const HistoryModal      = lazy(() => import('./components/modals/HistoryModal').then(m => ({ default: m.HistoryModal })));
const OnboardingWizard  = lazy(() => import('./components/onboarding/OnboardingWizard').then(m => ({ default: m.OnboardingWizard })));

export default function App() {
  const { S, act, flushOutbox } = useApp();
  const { toasts, showToast, dismissToast } = useToast();

  const online = useOnlineStatus();

  // APP_OPENED — 세션당 1회 발화 (마운트 시점)
  useEffect(() => {
    track(EVENTS.APP_OPENED, { onboarded: !!S.onboarded, taskCount: S.tasks.length });
    // reason: mount-only 1-shot analytics — deps 의 onboarded/tasks.length 변동은 의도적 무시.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 전역 에러 이벤트 → 토스트 (storage 저장 실패 · Supabase 동기화 실패) ──
  useGlobalErrorToasts(showToast);

  // 키보드 단축키 — 페이지 이동(g+키 시퀀스 제외, 일단 단일 키 / mod 콤보만)
  const shortcuts = useMemo(() => [
    { combo: 'mod+/',  handler: () => act('SET_UI', { patch: { modalShortcuts: true } }) },
    { combo: '?',      handler: () => act('SET_UI', { patch: { modalShortcuts: true } }) },
  ], [act]);
  useKeyboardShortcuts(shortcuts);

  const me = S.members.find(m => m.id === S.uid);
  const isMgr = canManageMembers(me);
  useNavGuard({ activeNav: S.activeNav, isManager: isMgr, act });

  // ── 안 읽음 카운트 → 탭 타이틀·favicon 배지·재진입 토스트 ──
  useUnreadBadge({ tasks: S.tasks, uid: S.uid, showToast });

  // ── Push 권한 프롬프트 + iOS 설치 안내 ──
  const { pushPromptOpen, onEnablePush, onDismissPush, installCardShow, onDismissInstall } =
    usePushPrompt({ uiPrefs: S.uiPrefs ?? {}, act, showToast });

  // ── 결제 상태 — SubscriptionBanner ──
  const { status: subStatus, daysLeft: subDaysLeft } = useSubscription();
  // 결제 배너 CTA → 설정 모달(플랜 선택 2티어)로. 구 단일 Stripe Checkout 직행 폐기.
  const onUpgrade = () => act('SET_UI', { patch: { modalSettings: true } });

  // ── 마운트 1회 housekeeping (반복생성·기한경과 이월·휴지통 정리) ──
  useBootHousekeeping({ tasks: S.tasks, act, showToast });

  return (
    <div className="app-shell">
      {/* ── 미전송 큐 + 오프라인 알림 — Topbar 위 sticky ── */}
      <OfflineIndicator
        outbox={S.outbox ?? []}
        online={online}
        onRetry={flushOutbox ?? (() => {})}
      />
      {/* ── 결제 상태 배너 (trial D-1·past_due·cancelled). active 시 미렌더 ── */}
      <SubscriptionBanner
        status={subStatus}
        daysLeft={subDaysLeft}
        onUpgrade={onUpgrade}
      />
      {/* ── 상단 네비게이션 바 ── */}
      <Topbar onToast={showToast} />
      <div className="app-body">
        {/* ── activeNav 에 따라 페이지 전환 (lazy 페이지는 Suspense 안으로) ──
            ErrorBoundary 가 lazy 청크 다운로드 실패를 잡아 재시도 UI 노출.
            key=activeNav 로 페이지 전환 시 경계 자동 리셋. */}
        <ErrorBoundary key={S.activeNav} message="페이지를 불러오지 못했습니다 — 네트워크 연결을 확인해 주세요">
          <Suspense fallback={<div className="suspense-loading" role="status" aria-live="polite"><LoadingSpinner size={18} /> 불러오는 중…</div>}>
            {S.activeNav === 'tasks'     && <TasksPage    onToast={showToast} />}
            {S.activeNav === 'members'   && <MembersPage onToast={showToast} />}
            {S.activeNav === 'mypage'    && <MyPage      onToast={showToast} />}
          </Suspense>
        </ErrorBoundary>
      </div>

      {/* ── 전역 모달 (S.modal* 플래그로 마운트/언마운트) — 모두 lazy. fallback=null 로 깜빡임 최소화 ──
          모달 청크 다운로드 실패 시 ErrorBoundary 가 토스트 형태 메시지 + 재시도 UI 노출. */}
      <ErrorBoundary message="모달을 불러오지 못했습니다 — 다시 열어 주세요">
        <Suspense fallback={null}>
          {S.modalTask     && <TaskModal     onToast={showToast} />}
          {S.modalRoutine  && <RoutineModal  onToast={showToast} />}
          {/* 이력는 ProgModal 보다 먼저 마운트 — 행 클릭 시 상세가 이력 위에 스택(같은 z, DOM 순서로 위) */}
          {S.modalHistory && <HistoryModal />}
          {S.modalProg     && <ProgModal     onToast={showToast} />}
          {S.modalTrash    && <TrashModal    onToast={showToast} />}
          {S.modalShortcuts && <ShortcutsHelpModal />}
          {S.modalInviteMember && <InviteMemberModal onToast={showToast} />}
          {S.modalSettings && <SettingsModal onToast={showToast} />}
        </Suspense>
      </ErrorBoundary>

      {/* ── 첫 진입 온보딩 위저드 — 셋업 완료(onboarded) 전까지 표시 ── */}
      {!S.onboarded && (
        <Suspense fallback={null}>
          <OnboardingWizard />
        </Suspense>
      )}

      {/* ── iOS PWA 설치 안내 + Push 권한 프롬프트 ── */}
      <InstallPwaCard show={installCardShow} onDismiss={onDismissInstall} />
      <PushPermissionPrompt
        open={pushPromptOpen}
        onEnable={onEnablePush}
        onDismiss={onDismissPush}
      />

      {/* ── 전역 토스트 알림 (큐 누적) ── */}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
